// Implements: F-VER-007, F-UIX-007, F-UIX-009, F-UIX-015
/**
 * The difference between two versions, one region per kind of piece. Pure
 * presentation.
 *
 * Every line keeps its own `+` or `-`: an added or removed line is told apart
 * by the sign as well as by the colour, never by the colour alone. A kind
 * with no difference says so in words instead of showing an empty box, and a
 * kind the dictionary has no word for keeps its own name.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { ArtifactDiffView } from "../../../types/api";
import { kindKey } from "./artifactKinds";
import styles from "./VersionDiff.module.css";

export interface VersionDiffProps {
  readonly pieces: readonly ArtifactDiffView[];
}
type LineKind = "header" | "hunk" | "added" | "removed" | "context";

const KIND_CLASS: Readonly<Record<LineKind, string | undefined>> = {
  header: styles.header,
  hunk: styles.hunk,
  added: styles.added,
  removed: styles.removed,
  context: undefined,
};

interface DiffLine {
  readonly kind: LineKind;
  readonly text: string;
}

/**
 * The lines of a unified diff, told apart.
 *
 * `---` and `+++` are file headers ONLY before the first hunk (`@@`): after
 * it, a line that starts with three dashes is a removed line whose content
 * starts with two. A diff that ends in a newline has no empty last line.
 */
function parseDiff(unified: string): readonly DiffLine[] {
  const raw = unified.split("\n");
  if (raw[raw.length - 1] === "") raw.pop();
  let inHunk = false;
  return raw.map((text): DiffLine => {
    if (text.startsWith("@@")) {
      inHunk = true;
      return { kind: "hunk", text };
    }
    if (!inHunk && (text.startsWith("---") || text.startsWith("+++"))) {
      return { kind: "header", text };
    }
    if (text.startsWith("+")) return { kind: "added", text };
    if (text.startsWith("-")) return { kind: "removed", text };
    return { kind: "context", text };
  });
}

export function VersionDiff({ pieces }: VersionDiffProps): ReactElement {
  const { t } = useTranslation();

  return (
    <>
      {pieces.map((piece) => {
        const key = kindKey(piece.kind);
        const name = key === undefined ? piece.kind : t(key);
        return (
          <section key={piece.kind} className={styles.piece} aria-label={name}>
            <h3 className={styles.kind}>{name}</h3>
            {piece.unified_diff === "" ? (
              <p className={styles.identical}>{t("compare.identical")}</p>
            ) : (
              <pre className={styles.diff}>
                {parseDiff(piece.unified_diff).map((line, index) => (
                  // The diff is positional text: the line number IS its identity.
                  <span key={index} className={KIND_CLASS[line.kind]} data-line={line.kind}>
                    {line.text}
                    {"\n"}
                  </span>
                ))}
              </pre>
            )}
          </section>
        );
      })}
    </>
  );
}
